// Replacing == use isn't worth the risk in this legacy file right now
/* eslint-disable eqeqeq */
import { type AjaxResponse } from '../../common/types/AjaxResponse';
import { isLoggedIn } from '../../utils/isLoggedIn';

/**
 * Keep in sync with ReferredSiteVisit.java
 */
interface Rsv {
    landingURL: string;
    rfParam: string;
    referrer: string;
    firstInSession: string;
    created: string;
}

interface CookiedURLValues {
    referrer?: string;
    landingURL?: string | null;
    created?: string | null;
}

type StoredReferredSiteVisits = Rsv[] | string | null;

const uninterestingReferrers = [
    'localhost',
    '.donorschoose.org',
    'https://www.paypal.com',
    '/cgi-bin/webscr?cmd=_flow',
    'https://authorize.payments.amazon.com',
];

export function isInterestingReferrer(referrer: string | nullish): boolean {
    return !!referrer && !uninterestingReferrers.some(uninteresting => referrer.includes(uninteresting));
}

function getSessionURLValues(): CookiedURLValues {
    return getCookiedURLValues(window.readCookie('sessionURL'));
}

function getCookiedURLValues(urlString: string | nullish): CookiedURLValues {
    if (urlString == null || urlString === '') return {};
    const urlStringArray = urlString.split('###');
    if (urlStringArray.length > 0) {
        const referrer = urlStringArray[0];
        const landingURL = (urlStringArray[1] != null) ? unescape(urlStringArray[1]) : null;
        const created = (urlStringArray[2] != null) ? unescape(urlStringArray[2]) : null;
        return { referrer, landingURL, created };
    }
    return {};
}

/*
 * POST size is limited to about 4K on local servers and about 50K elsewhere. We try to respect
 * those limits by capping the record count, so records much larger than the typical ~200
 * characters can still push a payload over the limit. Dropped records are silently discarded.
 */
const MAX_POST_CHARS = 50000;
const MAX_RECORDS = 250;
const MAX_POST_CHARS_LOCAL = 4000;
const MAX_RECORDS_LOCAL = 10;

/**
 * Truncates a referredSiteVisits array whose payload might exceed the server's post size limit,
 * keeping the first visit (first-touch attribution) plus the most recent ones.
 * Exported for testing.
 */
export function truncateReferredSiteVisits(referredSiteVisits: Rsv[]): Rsv[] {
    const isLocal = baseURL.indexOf('local') > -1;
    const maxChars = isLocal ? MAX_POST_CHARS_LOCAL : MAX_POST_CHARS;
    const maxRecords = isLocal ? MAX_RECORDS_LOCAL : MAX_RECORDS;
    if (JSON.stringify(referredSiteVisits).length > maxChars && referredSiteVisits.length > maxRecords) {
        return [referredSiteVisits[0]].concat(referredSiteVisits.slice(referredSiteVisits.length - (maxRecords - 1)));
    }
    return referredSiteVisits;
}

let rsvTimeout: ReturnType<typeof setTimeout> | undefined;
function getReferredSiteVisitForm(referredSiteVisits: Rsv[], uuid?: string): HTMLFormElement {
    const truncatedSiteVisits = JSON.stringify(truncateReferredSiteVisits(referredSiteVisits));

    $('#referredSiteVisitForm').remove();
    const formEl = document.createElement('form');
    formEl.className = 'hidden';
    formEl.method = 'post';
    formEl.name = 'referredSiteVisitForm';
    formEl.id = 'referredSiteVisitForm';
    formEl.action = secureBaseURL + 'common/referredSiteVisits.html';

    function addHiddenInput(name: string, value: string): void {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        formEl.appendChild(input);
    }

    addHiddenInput('referredSiteVisits', truncatedSiteVisits);
    addHiddenInput('callback', 'saveReferredSiteVisitCallback');
    addHiddenInput('postMessage', 'parent.postMessage');
    if (uuid) {
        addHiddenInput('uuid', uuid);
    }

    document.body.appendChild(formEl);

    // if we don't hear back from the server in 30 seconds, delete referredSiteVisit records and don't try again
    rsvTimeout = setTimeout(function () { console.log('Unresponsive server: referredSiteVisit records deleted.'); window.dcLocalStorage.setValue('referredSiteVisits', '[]'); }, (1000 * 30));
    return formEl;
}

// @ts-expect-error To allow us to set saveReferredSiteVisitCallback on window for JSONP callback.
// See ReferredSiteVisitUpdateController.java for the payload shape.
window.saveReferredSiteVisitCallback = function (response: AjaxResponse<{ success: boolean }> | undefined) {
    clearTimeout(rsvTimeout);
    if (response?.data?.success) {
        window.dcLocalStorage.setValue('referredSiteVisits', '[]');
    }
};

/**
 * Shared code to submit a referredSiteVisits array to ReferredSiteVisitUpdateController.
 *
 * Extracted from logReferredSiteVisit.
 * @param referredSiteVisits
 * @param uuid Optional string UUID referring to an emailcontactsignup with which this visit should be associated
 */
const syncReferredSiteVisits = (referredSiteVisits: Rsv[], uuid?: string): void => {
    /* SAFARI hack:
     * Cross-domain localStorage iframe no longer works because safari is checking
     * parent subdomain and storing there.  As a fallback, use cookies to populate values.
     */
    if (referredSiteVisits.length === 0) {
        const referringRSV = getRSVFromCookiedVisit('referringURL');
        if (referringRSV != null) referredSiteVisits.push(referringRSV);
        const sessionRSV = getRSVFromCookiedVisit('sessionURL');
        if (sessionRSV != null && (referringRSV == null || sessionRSV.created != referringRSV.created)) {
            referredSiteVisits.push(sessionRSV);
        }
    }

    setSaveAttemptedForCookiedVisit('referringURL');
    setSaveAttemptedForCookiedVisit('sessionURL');

    if (referredSiteVisits.length > 0) {
        const formEl = getReferredSiteVisitForm(referredSiteVisits, uuid);
        window.submitFormToIframe(formEl, 'referredSiteVisitFormIframe');
    }
};

/**
 * Normally, we conditionally sync to `referredsitevisit` when pages are loaded. If you want to imperatively sync
 * instead, call this function to try to send whatever's in localStorage to the server.
 */
export const forceSyncReferredSiteVisits = (uuid?: string): void => {
    const value = window.dcLocalStorage.getValue<StoredReferredSiteVisits>('referredSiteVisits');
    if (value == null || value === '') {
        // No referredSiteVisits in localStorage; nothing to sync
        return;
    }

    syncReferredSiteVisits(value as Rsv[], uuid);
};

function logReferredSiteVisit(genLandingURL: string, syncToServer: boolean): void {
    const landingURL = genLandingURL;
    let rfParam = window.getRfParamFromQueryStringOrHash(genLandingURL);

    // ignore rfParam if it looks like a teacher is referring themselves
    if (rfParam != null && rfParam.indexOf('teacher_') > -1 && window.userId != null && window.userId != '' && window.userId == window.getRfParamTeacherId(genLandingURL)) {
        rfParam = '';
    }

    const referrer = (typeof document.referrer !== 'undefined' && document.referrer != null) ? document.referrer : '';
    let firstInSession = false;

    // get date UTC
    const created = new Date();
    const createdMills = created.getTime();

    const rsv: Rsv = { landingURL, rfParam, referrer, firstInSession: firstInSession.toString(), created: createdMills.toString() };

    // As of approx July 10 2013, always write the referringURL and sessionURL cookies even if we think there isn't a valid referrer

    // first ever visit
    if (window.readCookie('referringURL') == null) {
        firstInSession = true;
        rsv.firstInSession = 'true';
        const locString = escape(referrer) + '###' + escape(landingURL) + '###' + createdMills;
        window.createCookie('referringURL', locString, 45);
    }

    // first in session
    if (window.readCookie('sessionURL') == null) {
        firstInSession = true;
        rsv.firstInSession = 'true';
        const locString = escape(referrer) + '###' + escape(landingURL) + '###' + createdMills;
        window.createCookie('sessionURL', locString, 0);
    }

    // double check that it's not just a page reload
    const savedRfParam = window.readCookie('rfParam');
    const currentSessionURL = getSessionURLValues();

    const hasNewReferredSiteVisit = (firstInSession || (rfParam != '' && (savedRfParam == null || savedRfParam != rfParam)) || (isInterestingReferrer(referrer) && referrer != currentSessionURL.referrer));

    const value = window.dcLocalStorage.getValue<StoredReferredSiteVisits>('referredSiteVisits');
    // Note that dcLocalStorage.getValue will attempt deserialization on its own. So `value` could
    // be either a string or an array even though we only ever *insert* strings.
    const referredSiteVisits: Rsv[] = (value != null && value !== '') ? (value as Rsv[]) : [];
    if (hasNewReferredSiteVisit) {
        referredSiteVisits.push(rsv);
        window.dcLocalStorage.setValue('referredSiteVisits', JSON.stringify(referredSiteVisits));
    }
    // write to db if we think the person already has a jsessionid and a userid

    const hasCartInteraction = (window.isNonBlankCookie('cartUUID') || window.isNonBlankCookie('cartGiftCodeAmount')) && window.userId;
    const isTeacherOnSecurePage = (window.userId && window.userObj && window.userObj.userType === 'teacher' && (window.location.hostname).indexOf('secure') > -1);

    if (syncToServer && (isLoggedIn() || isTeacherOnSecurePage || (hasCartInteraction))) {
        syncReferredSiteVisits(referredSiteVisits);
    }
    if (rfParam != '') {
        window.createCookie('rfParam', rfParam, 0);
    }
}

function getRSVFromCookiedVisit(cookieType: string): Rsv | null {
    const cookieValue = window.readCookie(cookieType);
    if (cookieValue != null) {
        const rsvArray = cookieValue.split('###');
        if (rsvArray.length === 3) {
            const landingURL = unescape(rsvArray[1]);
            let rfParam = '';
            const landingURLArray = landingURL.split('rf=');
            if (landingURLArray.length > 1) {
                rfParam = (landingURLArray[1].split('&'))[0];
            }
            return {
                landingURL,
                rfParam,
                referrer: unescape(rsvArray[0]),
                firstInSession: 'true',
                created: rsvArray[2],
            };
        }
    }
    return null;
}

function setSaveAttemptedForCookiedVisit(cookieType: string): void {
    const cookieValue = window.readCookie(cookieType);
    if (cookieValue != null) {
        const rsvArray = cookieValue.split('###');
        if (rsvArray.length === 3) {
            rsvArray.push('attemptedSave');
            window.createCookie(cookieType, rsvArray.join('###'), (cookieType === 'sessionURL') ? 0 : 45);
        }
    }
}

function setLastVisitedPage(): void {
    window.createCookie('lastVisitedPage', escape(location.href), 1);
}

export function setSiteVisit({ syncToServer = true } = {}): void {
    setLastVisitedPage();
    if (window.genLandingURL) {
        logReferredSiteVisit(window.genLandingURL, syncToServer);
    }
}
